import { KeyValue } from '../models';

interface QueryString {
  [key: string]:
    | string
    | string[]
    | number[]
    | number
    | boolean
    | KeyValue
    | null
    | undefined;
}

/**
 *
 * Funzione che si occupa di trimmare le stringhe all'interno di un array contenente parametri "Params" prima di inviarli alla request di una chiamata al BE.
 *
 * @param {any[] | undefined} params - I parametri della chiamata da passare alla request
 * @returns {any[]}
 */
export const trimStringsFromArray = (params: any[]): any[] => {
  return params.map((arrayParam: any) => {
    if (typeof arrayParam === 'string') return arrayParam.trim();
    if (Array.isArray(arrayParam)) return trimStringsFromArray(arrayParam);
    if (typeof arrayParam === 'object')
      return trimStringsFromParams(arrayParam);

    return arrayParam;
  });
};

/**
 *
 * Funzione che si occupa di trimmare le stringhe all'interno di un oggetto contenente parametri "Params" prima di inviarli alla request di una chiamata al BE.
 *
 * @param {{ [x: string]: any } | any[] | undefined} params - I parametri della chiamata da passare alla request
 * @returns {any}
 */
export const trimStringsFromParams = (
  params?: { [x: string]: any } | any[],
): any => {
  if (!params) return undefined;
  if (Array.isArray(params)) return trimStringsFromArray(params);

  const processedParams: { [x: string]: any } = {};

  Object.keys(params).forEach((key) => {
    if (typeof params[key] === 'string')
      processedParams[key] = params[key].trim();
    if (Array.isArray(params[key]))
      processedParams[key] = trimStringsFromArray(params[key]);
    if (typeof params[key] === 'object')
      processedParams[key] = trimStringsFromParams(params[key]);

    processedParams[key] = params[key];
  });

  return processedParams;
};

export const queryString = (params: QueryString = {}): string => {
  // get array of key value pairs ([[k1, v1], [k2, v2]])
  const qs = Object.entries(params)
    // filter pairs with undefined value and empty arrays
    .filter(
      (pair): boolean =>
        pair[1] !== undefined &&
        (!Array.isArray(pair[1]) || (pair[1] as any).length > 0),
    )
    // encode keys and values, remove the value if it is null, but leave the key
    .map((pair): string => {
      if (Array.isArray(pair[1])) {
        return pair[1]
          .filter((i): boolean => i !== null)
          .map((el: string | number | boolean | KeyValue) => {
            if (typeof el !== 'object') {
              return `${pair[0]}=`.concat(encodeURIComponent(el).trim());
            }
            return `${pair[0]}=`.concat(encodeURIComponent(el.id).trim());
          })
          .join('&');
      }
      return (
        pair
          .filter((i): boolean => i !== null)
          // @ts-ignore
          .map((el: string | number | boolean | KeyValue) => {
            if (typeof el !== 'object') {
              return encodeURIComponent(el).trim();
            }
            return encodeURIComponent(el.id).trim();
          })
          .join('=')
      );
    })
    .join('&');
  return qs && `?${qs}`;
};

export const sanitizeEmptyString = (obj: any): any => {
  const regex = /^[.,]+$/;
  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map(sanitizeEmptyString);
    } else {
      const newObj: any = {};
      for (const key in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(key)) {
          newObj[key] = sanitizeEmptyString(obj[key]);
        }
      }
      return newObj;
    }
  } else if (typeof obj === 'string' && (obj === '' || regex.test(obj))) {
    return null;
  } else {
    return obj;
  }
};
